import styled, { keyframes } from 'styled-components';
import { FaArrowLeft } from 'react-icons/fa';

const theme = {
  surface: '#252836',
  surfaceHover: '#2d3040',
  accent: '#ff79c6',
  text: '#f8f8f2',
  textSecondary: '#bd93f9',
  border: '#44475a',
  danger: '#ff5555',
  bg: '#1C1E2A',
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99000;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const ProfileCard = styled.div`
  background-color: ${theme.surface};
  color: ${theme.text};
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  width: 90%;
  max-width: 800px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.4s ease-out;
  border: 1px solid ${theme.border};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.border};
`;

export const BackIcon = styled(FaArrowLeft)`
  font-size: 1.5rem;
  color: ${theme.accent};
  cursor: pointer;
  transition: transform 0.2s ease;
  &:hover { transform: scale(1.1); }
`;

export const Title = styled.h1`
  font-size: 1.5rem;
  margin: 0;
  margin-left: 1.5rem;
`;

export const Content = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  border-bottom: 1px solid ${theme.border};
  margin-bottom: 2rem;
`;

export const Tab = styled.button`
  background: none;
  border: none;
  color: ${({ active }) => (active ? theme.accent : theme.textSecondary)};
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0 0.5rem 1rem 0.5rem;
  cursor: pointer;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: ${theme.accent};
    transform: ${({ active }) => (active ? 'scaleX(1)' : 'scaleX(0)')};
    transition: transform 0.3s ease;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  label {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${theme.textSecondary};
    text-transform: uppercase;
  }
  input, select {
    height: 42px;
    background-color: ${theme.bg};
    border: 1px solid ${theme.border};
    border-radius: 8px;
    color: ${theme.text};
    font-size: 0.9rem;
    padding: 0 1rem;
    box-sizing: border-box;
    transition: border-color 0.2s ease;
    &:focus {
      outline: none;
      border-color: ${theme.accent};
    }
    &:disabled {
      background-color: ${theme.surface};
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.border};
`;

export const Button = styled.button`
  padding: 0.7rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  &.primary {
    background-color: ${theme.accent};
    color: white;
  }
  &.secondary {
    background-color: ${theme.border};
    color: ${theme.text};
  }
  &:hover:not(:disabled) {
    filter: brightness(1.15);
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const toastAnimation = keyframes`
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  20% {
    transform: translateY(0);
    opacity: 1;
  }
  80% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(20px);
    opacity: 0;
  }
`;

export const Toast = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 99999;
  animation: ${toastAnimation} 3s ease-out forwards;
  
  background-color: ${({ type }) => (type === 'success' ? '#50fa7b' : '#ff5555')};
  color: ${({ type }) => (type === 'success' ? '#252836' : 'white')};
`;